import React from "react";
import { Typography, ProgressBar } from "../common";
import { Trophy, Clock, Target } from "lucide-react";
import { Course } from "../../types/types";

interface CourseJourneyInfoProps {
  courseTitle: string;
  courseIntro: string;
  lessonsCount: number;
  courseData?: Course; // reused container for course meta (progress, duration)
}

const CourseJourneyInfo: React.FC<CourseJourneyInfoProps> = ({
  courseTitle,
  courseIntro,
  lessonsCount,
  courseData,
}) => {
  // Calculate data from API (fields normalized in API slice)
  const estimatedTotalDurationMinutes = courseData?.estimated_total_duration_minutes ?? 0;
  const estimatedDurationHours = estimatedTotalDurationMinutes
    ? Math.round((estimatedTotalDurationMinutes / 60) * 10) / 10
    : "";
  const progressPercentage = courseData?.progress_percentage ?? 0;

  return (
    <div className="lg:col-span-1 space-y-6">
      {/* Course Info & Progress Card */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-grey-300">
        {/* Course Title & Description */}
        <div className="mb-6">
          <Typography
            variant="h2"
            weight="bold"
            className="text-textPrimary mb-3"
          >
            {courseTitle}
          </Typography>
          <Typography variant="p" className="text-textPrimary-grey">
            {courseIntro}
          </Typography>
        </div>

        {/* Progress Section */}
        <div className="mb-6">
          <div className="flex items-center justify-start mb-4">
            <Typography variant="h5" weight="bold" className="text-textPrimary">
              Progress
            </Typography>
          </div>
          <ProgressBar
            progress={progressPercentage}
            showTrophy={false}
            showProgressText={false}
            size="large"
          />
          <Typography variant="small" className="text-textPrimary-grey mt-2">
            {Math.round(progressPercentage)}% complete
          </Typography>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-2 gap-4">
          {/* Lessons Count */}
          <div className="bg-secondary-green rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-5 h-5 text-pd-ink" />
              <Typography
                variant="h4"
                weight="bold"
                className="text-pd-ink"
              >
                {lessonsCount}
              </Typography>
            </div>
            <Typography
              variant="small"
              className="text-pd-ink font-medium"
            >
              Total Units
            </Typography>
          </div>

          {/* Completed Lessons */}
          <div className="bg-secondary-blue rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <Trophy className="w-5 h-5 text-system-blue" />
              <Typography
                variant="h4"
                weight="bold"
                className="text-system-blue"
              >
                {courseData?.lessons?.filter((l: any) => l.status === 'completed').length || 0}
              </Typography>
            </div>
            <Typography
              variant="small"
              className="text-system-blue font-medium"
            >
              Completed
            </Typography>
          </div>

          {/* Duration Card */}
          <div className="bg-secondary-purple/50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-5 h-5 text-indigo-500" />
              <Typography
                variant="h4"
                weight="bold"
                className="text-indigo-500"
              >
                {estimatedDurationHours ? `${estimatedDurationHours}h` : "N/A"}
              </Typography>
            </div>
            <Typography variant="small" className="text-indigo-500 font-medium">
              Duration
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseJourneyInfo;
